import type { ProposalTemplate } from '@/context/MasterDataContext'

const esc = (s: string) =>
  (s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

/**
 * Build the default proposal body HTML from a template's structured clauses.
 * Used when a template has no custom (CKEditor) body yet. The output uses the
 * same CSS classes as the PDF shell and includes the {{PRICING_TABLE}} and
 * {{validityDate}} placeholders so the generator can inject live data.
 */
export const buildTemplateBody = (t: ProposalTemplate): string => {
  const bullets = (items: string[] = []) =>
    items.map((d) => `<div class="bullet">• ${esc(d)}</div>`).join('')

  return `
  <div class="intro">
    <strong>Sir,</strong><br><br>
    ${esc(t.introParagraph).replace(/\n/g, '<br><br>')}
  </div>

  <div class="section-heading">Please find below the Pricing:</div>
  {{PRICING_TABLE}}

  <div class="pricing-note">
    Price is inclusive of Transmission charges, system operation, scheduling Charges, transmission losses,
    cross-subsidy surcharge, Wheeling charges, DSM Charges, AMR readings, Import energy charges and additional surcharges.
  </div>

  ${t.powerFacilitationCharge ? `
  <div class="facilitation">
    Power facilitation charges of Rs. {POWER FACILITATION CHARGE}/- per unit plus applicable {GST ON FACILITATION CHARGE}% GST are exclusive of the above tariff.
  </div>` : ''}



  <div class="validity-box">
    ${esc(t.validityNote).replace(/\{VALIDITY_DATE\}/g, '<u>{{validityDate}}</u>')}
  </div>

  <div class="closing">
    ${esc(t.closingNote)}
  </div>`
}

/**
 * Resolve the body for a template: the manager's custom CKEditor body if present,
 * otherwise the default generated from the structured clauses.
 */
export const getTemplateBody = (t?: ProposalTemplate | null): string => {
  if (!t) return ''
  return t.bodyHtml && t.bodyHtml.trim() ? t.bodyHtml : buildTemplateBody(t)
}

export interface CoverPageData {
  title: string
  subtitle: string
  description?: string
  image?: string
}

/** A template's dynamic Cover Page config, or null when disabled/not set. */
export const getCoverPage = (t?: ProposalTemplate | null): CoverPageData | null => {
  if (!t || !t.coverPageEnabled) return null
  return {
    title: t.coverTitle || 'Commercial Proposal',
    subtitle: t.coverSubtitle || t.name || '',
    description: t.coverDescription || '',
    image: t.coverImage,
  }
}

/** A template's Company Content page (rich text, may include images) — '' if not set. */
export const getCompanyContent = (t?: ProposalTemplate | null): string =>
  t?.companyContentHtml && t.companyContentHtml.trim() ? t.companyContentHtml : ''

/**
 * Default Terms & Conditions HTML — built from the Commercial Terms tab
 * fields as {FIELD NAME} placeholders (not their resolved values), so the
 * text keeps tracking whatever the Commercial Terms tab currently holds
 * instead of freezing a stale snapshot the moment "Reset to default
 * clauses" is clicked.
 */
export const buildDefaultTermsHtml = (t: ProposalTemplate): string => {
  const rows = [
    '<p><strong>Billing:</strong> {BILL RAISE METHOD}</p>',
    '<p><strong>Payment:</strong> Due date: Payment to be made within {PAYMENT DUE IN DAYS} working days from the date of Invoice.</p>',
    '<p><strong>Payment Security:</strong> Not Applicable</p>',
    '<p><strong>Rate Revision:</strong> TNEB base tariff and any open access charges revision will be shared between the Consumer and power supplier on {RATE REVISION} basis.</p>',
    '<p><strong>Minimum Assured Supply:</strong> {MINIMUM ASSURED SUPPLY} of the contracted quantum</p>',
  ]
  if (t.energyAdjustmentClauses?.length) {
    rows.push('<p><strong>Energy Supply &amp; Adjustments:</strong></p>')
    rows.push('<ul>' + t.energyAdjustmentClauses.map((c) => `<li>${esc(c)}</li>`).join('') + '</ul>')
  }
  return rows.join('')
}

/** Resolve a template's Terms & Conditions: the manager's own CKEditor content
 *  if present, otherwise the default generated from the structured clauses. */
export const getTemplateTermsHtml = (t?: ProposalTemplate | null): string => {
  if (!t) return ''
  return t.termsConditionsHtml && t.termsConditionsHtml.trim() ? t.termsConditionsHtml : buildDefaultTermsHtml(t)
}
